import React, { useEffect, useState } from 'react';
import { HorizontalBarChart } from '../../../components/charts/horizontal-bar-chart/horizontal-bar-chart';
import { useAppActions, useAppState } from '../../../overmind';
import { EmptyState } from '../../../components/general/empty-state/empty-state';
import { ScoresSearchParams } from '../../../models/overmind/search-params';
import { DataPoint } from '../../../components/charts/recharts.models';
import { removeDuplicateScores } from '../../../components/charts/recharts.helpers';
import { SkeletonOverlay } from '../../../components/general/loading-state/skeleton-overlay';
import { WidgetCard } from '../analytics-filters/widget-card';
import { Pager } from '@keplerco/core';
import classNames from 'classnames';

export function SkillScoreExpectedProficiencyWidget({ baseSearchParams, personName }: { baseSearchParams: ScoresSearchParams, personName?: string }): JSX.Element {
  const actions = useAppActions();
  const { companyVariables } = useAppState();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [dataPoints, setDataPoints] = useState<DataPoint[]>();
  const [totalPages, setTotalPages] = useState<number>(0);
  const [searchParams, setSearchParams] = useState<ScoresSearchParams>(baseSearchParams);

  async function getData(searchParams: ScoresSearchParams): Promise<void> {
    const response = await actions.analyticsGetScoresByScoreType(searchParams);

    const uniqueResponse = removeDuplicateScores(response?.results ?? [], 'name');
    setDataPoints(
      uniqueResponse.map(entityScore => ({
        name: entityScore.name,
        'Average skill score': companyVariables.useLevels ? entityScore.score?.level : entityScore.score?.percentage,
        'Expected proficiency': entityScore.comparisonScore ? (companyVariables.useLevels ? entityScore.comparisonScore.level : entityScore.comparisonScore.percentage) : undefined,
      }))
    );
    setTotalPages(response?.totalPages ?? 0);
  }

  useEffect(() => {
    async function initialise() {
      setIsLoading(true);
      await getData(searchParams);
      setIsLoading(false);
    }

    initialise();
  }, [companyVariables.slug, baseSearchParams.roleSlug, baseSearchParams.learnerSlug]);

  return (
    <SkeletonOverlay isLoading={isLoading} width='100%'>
      <WidgetCard title={`${personName ? 'Skill' : 'Average skill'} score vs expected proficiency`} subtitle={`${personName ? `The skill score for ${personName} vs the role's expected proficiency` : 'An average of the skill scores for the people in this role, vs the expected proficiency of these skills for this role.'}`} showSkillLevelHelp>
        {!!dataPoints?.length ?
          <div>
            <HorizontalBarChart dataPoints={dataPoints} fill={['baby-blue', 'bright-pink']} showLegend />
            <div className={classNames({ invisible: totalPages < 2 })}>
              <Pager
                activePageNumber={searchParams.page ?? 1}
                pageCount={totalPages}
                onPageChange={async page => {
                  if (page === searchParams.page) return;
                  setIsLoading(true);
                  setSearchParams({ ...searchParams, page });
                  await getData({ ...searchParams, page });
                  setIsLoading(false);
                }}
              />
            </div>
          </div>
          : <EmptyState />}
      </WidgetCard>
    </SkeletonOverlay>
  );
}
